import { Signal, WritableSignal, linkedSignal } from '@angular/core';
import { DataSourceDevice, Installation } from '@core/models/installation.model';

export interface FedDevice {
  deviceId: number;
  ldn: string;
  serial: string | null;
}

export interface FedDeviceDetails extends FedDevice {
  typeName: string | null;
  onlineStatus: string | null;
  protocol: string | null;
  // A member of the installation that no point reads from yet.
  feedsNoPoint?: boolean;
}

// A device appears on the installation page only as the source of a point; everything else about
// it lives on the device page.
export function fedDevices(installation: Installation): FedDevice[] {
  const devices = new Map<number, FedDevice>();
  for (const point of installation.points ?? []) {
    const source = point.binding?.source;
    if (source?.kind === 'device_channel' && source.deviceId !== null) {
      devices.set(source.deviceId, {
        deviceId: source.deviceId,
        ldn: source.ldn ?? String(source.deviceId),
        serial: source.serial,
      });
    }
  }
  return [...devices.values()];
}

// The data sources list the devices at the installation's location, which need not be the devices
// that feed it, so a fed device is matched by id and keeps no details when none is listed. A member
// device that feeds no point yet is not a fed device, but belongs on the list all the same.
export function withDeviceDetails(
  devices: FedDevice[],
  sources: DataSourceDevice[],
): FedDeviceDetails[] {
  const fed = devices.map(device => {
    const source = sources.find(candidate => candidate.deviceId === device.deviceId);
    return {
      ...device,
      typeName: source?.deviceTypeName ?? null,
      onlineStatus: source?.onlineStatus ?? null,
      protocol: source?.protocol ?? null,
    };
  });
  const idle = sources
    .filter(source => source.member && !devices.some(device => device.deviceId === source.deviceId))
    .map(source => ({
      deviceId: source.deviceId,
      ldn: source.ldn ?? String(source.deviceId),
      serial: source.serial,
      typeName: source.deviceTypeName ?? null,
      onlineStatus: source.onlineStatus ?? null,
      protocol: source.protocol,
      feedsNoPoint: true,
    }));
  return [...fed, ...idle];
}

// The device a tab shows when several feed the installation: the first, until the user picks
// another, which stays picked for as long as it still feeds the installation.
export function deviceSelection(devices: Signal<FedDevice[]>): WritableSignal<number | null> {
  return linkedSignal<FedDevice[], number | null>({
    source: devices,
    computation: (current, previous) =>
      current.find(device => device.deviceId === previous?.value)?.deviceId ??
      current[0]?.deviceId ??
      null,
  });
}
